import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Heart, FolderOpen } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog } from '../../components/Modal';
import EmptyState from '../../components/EmptyState';

const AdminContentInterests = () => {
  const [interests, setInterests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'interest' or 'category'
  const [selectedInterest, setSelectedInterest] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [interestFormData, setInterestFormData] = useState({ categoryId: '', name: '', value: '', isActive: true });
  const [categoryFormData, setCategoryFormData] = useState({ name: '', keyName: '', emoji: '', displayOrder: 0 });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [interestsRes, categoriesRes] = await Promise.all([
        api.get('/admin/content/interests'),
        api.get('/admin/content/interest-categories')
      ]);
      
      if (interestsRes.data.success) {
        // Transform is_active (0/1) to isActive (boolean)
        const transformedInterests = (interestsRes.data.interests || []).map(interest => ({
          ...interest,
          isActive: interest.is_active === 1 || interest.is_active === true || interest.isActive === true
        }));
        setInterests(transformedInterests);
      }
      if (categoriesRes.data.success) {
        setCategories(categoriesRes.data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load interests');
    } finally {
      setLoading(false);
    }
  };

  const openAddInterestModal = () => {
    if (categories.length === 0) {
      toast.error('Please create a category first');
      return;
    }
    setSelectedInterest(null);
    setInterestFormData({ categoryId: categories[0]?.id || '', name: '', value: '', isActive: true });
    setShowInterestModal(true);
  };

  const openEditInterestModal = (interest) => {
    setSelectedInterest(interest);
    const isActive = interest.is_active === 1 || interest.is_active === true || interest.isActive === true;
    setInterestFormData({
      categoryId: interest.category_id || '',
      name: interest.name || '',
      value: interest.value || '',
      isActive: isActive,
    });
    setShowInterestModal(true);
  };

  const openAddCategoryModal = () => {
    setSelectedCategory(null);
    setCategoryFormData({ name: '', keyName: '', emoji: '', displayOrder: 0 });
    setShowCategoryModal(true);
  };

  const openEditCategoryModal = (category) => {
    setSelectedCategory(category);
    setCategoryFormData({
      name: category.name || '',
      keyName: category.key_name || '',
      emoji: category.emoji || '',
      displayOrder: category.display_order || 0,
    });
    setShowCategoryModal(true);
  };

  const handleSaveInterest = async () => {
    if (!interestFormData.name.trim() || !interestFormData.value.trim() || !interestFormData.categoryId) {
      toast.error('Category, name, and value are required');
      return;
    }

    try {
      setSaving(true);
      const url = selectedInterest 
        ? `/admin/content/interests/${selectedInterest.id}`
        : '/admin/content/interests';
      const method = selectedInterest ? 'put' : 'post';
      
      const response = await api[method](url, {
        categoryId: interestFormData.categoryId,
        name: interestFormData.name,
        value: interestFormData.value,
        isActive: interestFormData.isActive
      });
      
      if (response.data.success) {
        toast.success(selectedInterest ? 'Interest updated successfully' : 'Interest created successfully');
        setShowInterestModal(false);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save interest');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryFormData.name.trim() || !categoryFormData.keyName.trim()) {
      toast.error('Category name and key are required');
      return;
    }

    try {
      setSaving(true);
      const url = selectedCategory 
        ? `/admin/content/interest-categories/${selectedCategory.id}`
        : '/admin/content/interest-categories';
      const method = selectedCategory ? 'put' : 'post';
      
      const response = await api[method](url, {
        name: categoryFormData.name,
        keyName: categoryFormData.keyName,
        emoji: categoryFormData.emoji,
        displayOrder: categoryFormData.displayOrder
      });
      
      if (response.data.success) {
        toast.success(selectedCategory ? 'Category updated successfully' : 'Category created successfully');
        setShowCategoryModal(false);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteConfirm = (item, type) => {
    if (type === 'interest') {
      setSelectedInterest(item);
      setDeleteType('interest');
    } else {
      setSelectedCategory(item);
      setDeleteType('category');
    }
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      let url;
      if (deleteType === 'interest') {
        url = `/admin/content/interests/${selectedInterest.id}`;
      } else {
        url = `/admin/content/interest-categories/${selectedCategory.id}`;
      }
      
      const response = await api.delete(url);
      if (response.data.success) {
        toast.success(`${deleteType === 'interest' ? 'Interest' : 'Category'} deleted successfully`);
        setShowDeleteModal(false);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to delete ${deleteType}`);
    } finally {
      setDeleting(false);
    }
  };

  // Group interests by category
  const groupedInterests = interests.reduce((acc, interest) => {
    const categoryKey = interest.category_key || 'uncategorized';
    if (!acc[categoryKey]) {
      acc[categoryKey] = {
        category: {
          id: interest.category_id,
          name: interest.category_name,
          key: interest.category_key,
          emoji: interest.category_emoji
        },
        items: []
      };
    }
    acc[categoryKey].items.push(interest);
    return acc;
  }, {});

  // Add categories with no interests
  categories.forEach(cat => {
    if (!groupedInterests[cat.key_name]) {
      groupedInterests[cat.key_name] = {
        category: {
          id: cat.id,
          name: cat.name,
          key: cat.key_name,
          emoji: cat.emoji
        },
        items: []
      };
    }
  });

  const filteredGrouped = Object.keys(groupedInterests).reduce((acc, key) => {
    const group = groupedInterests[key];
    const filtered = group.items.filter(interest =>
      interest.name?.toLowerCase().includes(search.toLowerCase()) ||
      interest.category_name?.toLowerCase().includes(search.toLowerCase())
    );
    if (filtered.length > 0 || group.category.name?.toLowerCase().includes(search.toLowerCase())) {
      acc[key] = { ...group, items: filtered };
    }
    return acc;
  }, {});

  return (
    <AdminSidebar>
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-1 sm:mb-2">
            Story Interests
          </h1>
          <p className="text-sm sm:text-base text-gray-600">Manage interest categories and interests</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={openAddCategoryModal}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg"
          >
            <FolderOpen className="h-5 w-5" />
            Add Category
          </button>
          <button
            onClick={openAddInterestModal}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
          >
            <Plus className="h-5 w-5" />
            Add Interest
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="relative">
          <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search interests or categories..."
            className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base transition-all"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : Object.keys(filteredGrouped).length === 0 ? (
        <EmptyState
          icon={<Heart className="h-7 w-7" />}
          title={search ? `No interests found for "${search}"` : 'No interests yet'}
          description={search ? 'Try adjusting your search terms' : 'Create your first category and interest to get started'}
          action={search ? null : { label: 'Add Category', onClick: openAddCategoryModal }}
        />
      ) : (
        <div className="space-y-6">
          {Object.keys(filteredGrouped).map((categoryKey) => {
            const group = filteredGrouped[categoryKey];
            const category = categories.find(c => c.key_name === categoryKey);
            return (
              <div key={categoryKey} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{group.category.emoji}</div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{group.category.name}</h3>
                      <p className="text-sm text-gray-500">{group.items.length} interests</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditCategoryModal(category)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit Category"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openDeleteConfirm(category, 'category')}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete Category"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {group.items.map((interest) => (
                    <div
                      key={interest.id}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{interest.name}</p>
                        <p className="text-xs text-gray-500">Value: {interest.value}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            interest.isActive
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {interest.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <button
                          onClick={() => openEditInterestModal(interest)}
                          className="p-1.5 text-purple-600 hover:bg-purple-100 rounded transition-colors"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openDeleteConfirm(interest, 'interest')}
                          className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {group.items.length === 0 && (
                    <div className="col-span-full text-center py-4 text-gray-500">
                      No interests in this category yet. Click "Add Interest" to create one.
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Interest Modal */}
      {showInterestModal && (
        <Modal
          title={selectedInterest ? 'Edit Interest' : 'Add Interest'}
          onClose={() => setShowInterestModal(false)}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={interestFormData.categoryId}
                onChange={(e) => setInterestFormData({ ...interestFormData, categoryId: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                required
              >
                <option value="">Select category...</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.emoji} {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Interest Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={interestFormData.name}
                onChange={(e) => setInterestFormData({ ...interestFormData, name: e.target.value, value: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                placeholder="e.g., Dogs, Soccer, Painting"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={interestFormData.value}
                onChange={(e) => setInterestFormData({ ...interestFormData, value: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                placeholder="e.g., Dogs, Soccer, Painting"
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Active Status</span>
              <button
                onClick={() => setInterestFormData({ ...interestFormData, isActive: !interestFormData.isActive })}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  interestFormData.isActive
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {interestFormData.isActive ? 'Active' : 'Inactive'}
              </button>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => setShowInterestModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveInterest}
                disabled={saving}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-60"
              >
                {saving ? 'Saving...' : selectedInterest ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <Modal
          title={selectedCategory ? 'Edit Category' : 'Add Category'}
          onClose={() => setShowCategoryModal(false)}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={categoryFormData.name}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                placeholder="e.g., Animals, Sports, Music"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Key Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={categoryFormData.keyName}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, keyName: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                placeholder="e.g., animals, sports, music"
              />
              <p className="text-xs text-gray-500 mt-1">Lowercase, no spaces (e.g., animals, sports)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Emoji</label>
              <input
                type="text"
                value={categoryFormData.emoji}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, emoji: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                placeholder="e.g., 🐾, ⚽, 🎵"
                maxLength="2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
              <input
                type="number"
                value={categoryFormData.displayOrder}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, displayOrder: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                min="0"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => setShowCategoryModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCategory}
                disabled={saving}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 disabled:opacity-60"
              >
                {saving ? 'Saving...' : selectedCategory ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <ConfirmDialog
          title={`Delete ${deleteType === 'interest' ? 'Interest' : 'Category'}`}
          description={`Are you sure you want to delete "${deleteType === 'interest' ? selectedInterest?.name : selectedCategory?.name}"? This action cannot be undone.${deleteType === 'category' ? ' All interests in this category will also be deleted.' : ''}`}
          confirmLabel={deleting ? 'Deleting...' : 'Yes, Delete'}
          onConfirm={handleDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedInterest(null);
            setSelectedCategory(null);
            setDeleteType(null);
          }}
        />
      )}
    </AdminSidebar>
  );
};

export default AdminContentInterests;
